import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { Clock, Coffee, Pause, Play, LogOut, LogIn, Users, DollarSign, AlertTriangle, CheckCircle, RotateCcw, Edit3, Target, Zap, Flag, Settings, TrendingUp, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useAuth } from "./App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTIVITY_TYPES = [
  // Daily shift start activity (Joao / Gabi morning opening checklist).
  "Morning Opening Routine",
  // Primary office activities shown after the morning routine / 9am.
  "Internal Calls", "External Calls", "National Calls", "Incoming Calls",
  "Safeguard Email", "Client Email", "Voicemail Review", "SMS Replies",
  "Priority Queue", "Inspector Support",
  // Legacy / role-specific kept so Ana / Vitória and older records still work.
  "SMS", "Phone Call", "Urgent Call", "Extra Activity", "Transmission",
];

// Activities that are LOGGED but do NOT contribute to office billable hours.
// (Transmission is paid per-order separately.)
const NON_BILLABLE_ACTIVITIES = new Set(["Transmission"]);

const STATUS_COLORS = {
  "Active": { bg: "#A7C4A0", text: "#1F2937", label: "ACTIVE" },
  "On Lunch": { bg: "#F5B971", text: "#1F2937", label: "ON LUNCH" },
  "On Pause": { bg: "#D1A054", text: "#1F2937", label: "ON PAUSE" },
  "Logged Out": { bg: "#9CA3AF", text: "#FFFFFF", label: "LOGGED OUT" },
};

function fmtMin(m) {
  m = Math.max(0, Math.round(m || 0));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${mm}m`;
}
function fmtDuration(startIso) {
  if (!startIso) return "0:00";
  const start = new Date(startIso);
  const diff = Math.floor((Date.now() - start.getTime()) / 1000);
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function OfficeWorkforcePage() {
  const { user } = useAuth();
  const isAdmin = ["Admin", "Finance"].includes(user?.role);
  const [tab, setTab] = useState("live");
  return (
    <div className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1F2937]">Office Workforce Control</h1>
            <p className="text-xs text-gray-500">Payroll-impacting time tracking · Boston timezone</p>
          </div>
          <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-0.5" data-testid="office-tabs">
            {[
              { k: "live", label: "Live Work", icon: Clock },
              { k: "history", label: "My History", icon: Users },
              ...(isAdmin ? [
                { k: "dashboard", label: "Live Dashboard", icon: Users },
                { k: "productivity", label: "Productivity", icon: Target },
                { k: "payroll", label: "Payroll", icon: DollarSign },
              ] : []),
            ].map(t => {
              const Icon = t.icon;
              return (
                <button
                  key={t.k}
                  onClick={() => setTab(t.k)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md flex items-center gap-1.5 transition-colors ${tab === t.k ? "bg-[#2F5D8A] text-white" : "text-gray-600 hover:bg-gray-100"}`}
                  data-testid={`office-tab-${t.k}`}
                >
                  <Icon className="h-3.5 w-3.5" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
        {tab === "live" && <LiveWorkPanel isAdmin={isAdmin} />}
        {tab === "history" && <HistoryPanel />}
        {isAdmin && tab === "dashboard" && <AdminDashboardPanel />}
        {isAdmin && tab === "productivity" && <ProductivityPanel />}
        {isAdmin && tab === "payroll" && <PayrollPanel />}
      </div>
  );
}

/* ══════════════════════════════ LIVE WORK (each employee) ══════════════════════════════ */
function LiveWorkPanel({ isAdmin }) {
  const location = useLocation();
  const gateTriggered = location?.state?.reason === "clock_in_required";
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState("Phone Call");
  const [comment, setComment] = useState("");
  const [msg, setMsg] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  // End-of-day summary modal (Ana / Vitória / office-task users)
  const [eodOpen, setEodOpen] = useState(false);
  const [eodText, setEodText] = useState("");
  const [eodSubmitting, setEodSubmitting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/office/my-status`);
      setState(r.data);
      if (r.data?.current_segment?.activity_type) {
        setSelectedActivity(r.data.current_segment.activity_type);
      } else {
        // First allowed activity if user has a whitelist, else fall back to Phone Call.
        const allowed = r.data?.user?.allowed_activities || [];
        if (allowed.length > 0) setSelectedActivity(allowed[0]);
      }
    } catch (e) {
      setMsg({ type: "error", text: "Failed to load status" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const act = async (path, body = null) => {
    try {
      setMsg(null);
      await axios.post(`${API}/office${path}`, body || {});
      await fetchStatus();
      // Gate cache: set when clocking in; clear on clock-out
      if (path === "/clock-in") {
        try { sessionStorage.setItem("office_clock_gate_ok", "yes"); } catch {}
      }
      if (path === "/clock-out") {
        try { sessionStorage.removeItem("office_clock_gate_ok"); } catch {}
      }
      setMsg({ type: "success", text: `${path.replace("/", "").replace("-", " ")} OK` });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setMsg({ type: "error", text: e.response?.data?.detail || "Action failed" });
    }
  };

  if (loading) return <div className="text-center py-8 text-gray-400">Loading...</div>;
  if (!state) return <div className="text-center py-8 text-gray-400">No data</div>;

  const status = state.status || "Logged Out";
  const badge = STATUS_COLORS[status] || STATUS_COLORS["Logged Out"];
  const segStart = state.current_segment?.start_time;
  const clockIn = state.time_log?.clock_in;
  const clockOut = state.time_log?.clock_out;
  const isClockedIn = !!clockIn && !clockOut;
  const isOnLunch = status === "On Lunch";
  const isOnPause = status === "On Pause";
  const currentActivity = state.current_segment?.activity_type;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="office-live-panel">
      {gateTriggered && !isClockedIn && (
        <div className="md:col-span-3 bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-start gap-2" data-testid="clock-in-required-banner">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-sm text-amber-800">Clock In required</p>
            <p className="text-xs text-amber-700">You must Clock In and select your activity before you can access the rest of the system.</p>
          </div>
        </div>
      )}
      {/* Left: Status + live timers */}
      <div className="md:col-span-2 bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider">Employee</p>
            <h2 className="text-lg font-bold text-[#1F2937]">{state.user.name}</h2>
            <p className="text-[10px] text-gray-500">{state.work_date} · Office Team Member · Boston Timezone</p>
          </div>
          <span className="px-3 py-1.5 rounded-full text-xs font-bold" style={{ backgroundColor: badge.bg, color: badge.text }} data-testid="office-status-badge">
            {badge.label}
          </span>
        </div>

        {state.current_segment && !clockOut && (
          <div className={`rounded-lg p-3 flex items-center justify-between ${
            NON_BILLABLE_ACTIVITIES.has(state.current_segment.activity_type)
              ? "bg-[#F3E8F5] border border-[#B07CC4]/40"
              : "bg-gray-50"
          }`}>
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">Current Activity</p>
              <p className="text-sm font-semibold text-[#1F2937] flex items-center gap-1.5">
                {state.current_segment.activity_type || status}
                {NON_BILLABLE_ACTIVITIES.has(state.current_segment.activity_type) && (
                  <span className="px-1.5 py-0.5 bg-[#B07CC4] text-white rounded text-[9px] font-bold" data-testid="transmission-badge">
                    PAID PER ORDER
                  </span>
                )}
              </p>
              {state.current_segment.comment && (
                <p className="text-[10px] text-gray-500 mt-0.5 italic">"{state.current_segment.comment}"</p>
              )}
              {NON_BILLABLE_ACTIVITIES.has(state.current_segment.activity_type) && (
                <p className="text-[10px] text-[#7B4D8E] mt-0.5 font-semibold">
                  Transmission Mode — tracked separately · paid per order · full system access available.
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">In Status</p>
              <p className="text-lg font-mono font-bold text-[#2F5D8A]" data-testid="office-live-timer">
                {nowTick && fmtDuration(segStart)}
              </p>
              {typeof state.completed_activities_today === "number" && state.completed_activities_today > 0 && (
                <p className="text-[9px] text-[#4A7A56] font-semibold mt-0.5" data-testid="completed-today-count">
                  ✓ {state.completed_activities_today} completed today
                </p>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center">
          {(() => {
            // Compute LIVE elapsed for the currently-open segment so both the
            // Billable tile and the Transmission tile tick every second while
            // Vitória works — without ever cross-crediting (billable vs transmission).
            const liveTransmission = state.totals?.total_transmission_minutes || 0;
            const liveBillable = state.totals?.total_paid_minutes || 0;
            let livePaid = liveBillable;
            let liveTrans = liveTransmission;
            const seg = state.current_segment;
            if (seg && !clockOut && seg.status_type === "Active" && seg.start_time) {
              const elapsed = Math.max(0, (nowTick - new Date(seg.start_time).getTime()) / 60000);
              if (NON_BILLABLE_ACTIVITIES.has(seg.activity_type)) liveTrans += elapsed;
              else livePaid += elapsed;
            }
            const showTransmission = liveTransmission > 0 || state.user?.allowed_activities?.includes("Transmission");
            const tiles = [
              { label: "Login Today", mins: state.totals?.total_login_minutes || 0, color: "#2F5D8A" },
              { label: "Productive Billable Hours", mins: livePaid, color: "#2F7D32", highlight: true },
              { label: "Lunch Time (unpaid)", mins: state.totals?.total_lunch_minutes || 0, color: "#9A6B2F", suffix: " 🍽️" },
              { label: "Pause", mins: state.totals?.total_pause_minutes || 0, color: "#8E8E8E" },
              ...(showTransmission
                ? [{ label: "Transmission (paid per order)", mins: liveTrans, color: "#7B4D8E", transmission: true }]
                : []),
            ];
            return tiles.map(stat => (
              <div key={stat.label} className={`rounded-lg p-2 ${
                stat.highlight ? "bg-green-50 border border-green-200"
                : stat.transmission ? "bg-[#F3E8F5] border border-[#B07CC4]/30"
                : "bg-gray-50"
              }`} data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, "-").replace(/[()]/g,"")}`}>
                <p className="text-[9px] text-gray-500 uppercase tracking-wider leading-tight">{stat.label}</p>
                <p className="text-sm font-bold" style={{ color: stat.color }}>{fmtMin(stat.mins)}{stat.suffix || ""}</p>
              </div>
            ));
          })()}
        </div>

        <OpeningShiftCard />
        <DailyAssignmentCard isAdmin={isAdmin} />

        <EarningsLiveTicker state={state} nowTick={nowTick} />

        {isClockedIn && !isOnLunch && !isOnPause && currentActivity && (
          <ProductivityLiveCard activityType={currentActivity} onMsg={setMsg} />
        )}

        {msg && (
          <div className={`p-2.5 rounded-lg text-xs flex items-center gap-1.5 ${msg.type === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`} data-testid="office-msg">
            {msg.type === "success" ? <CheckCircle className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            {msg.text}
          </div>
        )}
      </div>

      {/* Right: Controls */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
        <div>
          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Activity</label>
          {(() => {
            const opts = state?.user?.allowed_activities?.length > 0
              ? ACTIVITY_TYPES.filter(a => state.user.allowed_activities.includes(a))
              : ACTIVITY_TYPES;
            const disabled = isOnLunch || isOnPause;

            // Shared renderer — small pill button for one activity.
            const renderBtn = (a) => {
              const active = a === selectedActivity;
              const isTrans = NON_BILLABLE_ACTIVITIES.has(a);
              const isMorning = a === "Morning Opening Routine";
              const label = a === "SMS Replies" ? "SMS Replies / Reschedules" : a;
              return (
                <button
                  key={a}
                  type="button"
                  onClick={() => !disabled && setSelectedActivity(a)}
                  disabled={disabled}
                  data-testid={`activity-btn-${a.replace(/ /g,'-').toLowerCase()}`}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-[13px] font-semibold transition-all disabled:opacity-40 ${
                    active
                      ? isTrans
                        ? "bg-[#B07CC4] text-white border-[#B07CC4] shadow-sm"
                        : isMorning
                          ? "bg-[#C98F3A] text-white border-[#C98F3A] shadow-sm"
                          : "bg-[#2F5D8A] text-white border-[#2F5D8A] shadow-sm"
                      : isTrans
                        ? "bg-[#FBF5FD] text-[#7B4D8E] border-[#B07CC4]/40 hover:border-[#B07CC4] hover:bg-[#F3E8F5]"
                        : isMorning
                          ? "bg-[#FDF5E7] text-[#8A5A1F] border-[#C98F3A]/40 hover:border-[#C98F3A] hover:bg-[#FAE8C6]"
                          : "bg-white text-gray-700 border-gray-200 hover:border-[#2F5D8A]/40 hover:bg-[#2F5D8A]/5"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{label}</span>
                    {active && <CheckCircle className="h-4 w-4" />}
                  </div>
                  {a === "Extra Activity" && (
                    <p className={`text-[10px] font-normal mt-0.5 ${active ? "text-white/80" : "text-gray-400"}`}>
                      Miscellaneous / admin work — requires description
                    </p>
                  )}
                  {a === "Transmission" && (
                    <p className={`text-[10px] font-normal mt-0.5 ${active ? "text-white/80" : "text-[#7B4D8E]/70"}`}>
                      Full system access · time tracked separately · paid per order
                    </p>
                  )}
                  {isMorning && (
                    <p className={`text-[10px] font-normal mt-0.5 ${active ? "text-white/80" : "text-[#8A5A1F]/70"}`}>
                      8:00 – 9:00 · daily opening checklist
                    </p>
                  )}
                </button>
              );
            };

            // João / Gabriela grouped view — identified by the presence of
            // "Morning Opening Routine" in their allowed list.
            if (opts.includes("Morning Opening Routine")) {
              const GROUPS = [
                { title: "Opening Shift", items: ["Morning Opening Routine"] },
                { title: "Active Calls", items: ["Internal Calls", "External Calls", "National Calls"] },
                { title: "Receptive / Support", items: ["Incoming Calls", "Inspector Support", "Priority Queue"] },
                { title: "Communication / Follow-up", items: ["Client Email", "Safeguard Email", "Voicemail Review", "SMS Replies"] },
              ];
              return (
                <div className="space-y-3" data-testid="office-activity-grouped">
                  {GROUPS.map(g => {
                    const groupOpts = g.items.filter(a => opts.includes(a));
                    if (groupOpts.length === 0) return null;
                    return (
                      <div key={g.title}>
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 pl-0.5" data-testid={`activity-group-title-${g.title.replace(/[^a-z0-9]/gi,'-').toLowerCase()}`}>
                          {g.title}
                        </div>
                        <div className="grid grid-cols-1 gap-1.5">
                          {groupOpts.map(renderBtn)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }

            // Small whitelist (Ana / Vitória) — simple stacked buttons.
            if (opts.length <= 4) {
              return (
                <div className="grid grid-cols-1 gap-2" data-testid="office-activity-buttons">
                  {opts.map(renderBtn)}
                </div>
              );
            }

            // Fallback for any other user with a larger custom list.
            return (
              <select
                value={selectedActivity}
                onChange={e => setSelectedActivity(e.target.value)}
                disabled={isOnLunch || isOnPause || !isClockedIn}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm disabled:bg-gray-100"
                data-testid="office-activity-select"
              >
                {opts.map(a => <option key={a} value={a}>{a === "SMS Replies" ? "SMS Replies / Reschedules" : a}</option>)}
              </select>
            );
          })()}
        </div>
        <div>
          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
            {selectedActivity === "Extra Activity"
              ? <>Description <span className="text-[#C07070]">*</span></>
              : "Comment (optional)"}
          </label>
          <input
            type="text"
            value={comment}
            onChange={e => setComment(e.target.value)}
            maxLength={200}
            placeholder={selectedActivity === "Extra Activity"
              ? "e.g. updating inspector accounts, adding client info…"
              : ""}
            className={`w-full px-3 py-2 border rounded text-sm ${
              selectedActivity === "Extra Activity" && comment.trim().length < 5
                ? "border-[#C07070]/60 focus:border-[#C07070]"
                : "border-gray-300"
            }`}
            data-testid="office-comment-input"
          />
          {selectedActivity === "Extra Activity" && comment.trim().length < 5 && (
            <p className="text-[10px] text-[#C07070] mt-0.5">Required for Extra Activity (min. 5 characters)</p>
          )}
        </div>

        {!isClockedIn && (
          <button
            onClick={() => act("/clock-in", { activity_type: selectedActivity, comment })}
            disabled={!selectedActivity || (selectedActivity === "Extra Activity" && comment.trim().length < 5)}
            className="w-full py-2.5 bg-[#A7C4A0] hover:bg-[#8FB388] text-white text-sm font-bold rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-40"
            data-testid="office-btn-clock-in"
          >
            <LogIn className="h-4 w-4" /> {clockOut ? "START NEW SESSION" : "CLOCK IN"}
          </button>
        )}

        {isClockedIn && status === "Active" && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={async () => { if (window.confirm("Mark current activity as Completed?")) await act("/complete-activity"); }}
                className="py-2 bg-[#4A7A56] hover:bg-[#3a6245] text-white text-sm font-bold rounded-lg flex items-center justify-center gap-1.5"
                data-testid="office-btn-complete-activity"
              >
                <CheckCircle className="h-4 w-4" /> Activity Completed
              </button>
              <button
                onClick={() => act("/change-activity", { activity_type: selectedActivity, comment })}
                disabled={selectedActivity === "Extra Activity" && comment.trim().length < 5}
                className="py-2 bg-[#2F5D8A] hover:bg-[#24496e] text-white text-sm font-bold rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-40"
                data-testid="office-btn-change-activity"
              >
                <RotateCcw className="h-4 w-4" /> Switch
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => act("/lunch-out")} className="py-2 bg-[#F5B971] hover:bg-[#E6A85C] text-[#1F2937] text-xs font-bold rounded-lg flex items-center justify-center gap-1" data-testid="office-btn-lunch-out">
                <Coffee className="h-3.5 w-3.5" /> Lunch Out
              </button>
              <button onClick={() => act("/pause")} className="py-2 bg-[#D1A054] hover:bg-[#B08A3E] text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1" data-testid="office-btn-pause">
                <Pause className="h-3.5 w-3.5" /> Pause
              </button>
            </div>
            <button
              onClick={() => {
                if (state?.user?.requires_eod_summary && state?.day_has_extra_activity) {
                  setEodText("");
                  setEodOpen(true);
                } else if (window.confirm("Clock out for the day?")) {
                  act("/clock-out");
                }
              }}
              className="w-full py-2 bg-[#8B4545] hover:bg-[#6f3636] text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1.5"
              data-testid="office-btn-clock-out"
            >
              <LogOut className="h-3.5 w-3.5" /> CLOCK OUT
            </button>
          </>
        )}

        {/* Idle state: clocked in but no active activity — force user to pick the next one. */}
        {isClockedIn && status === "Idle" && (
          <>
            <div className="bg-[#FEF9E7] border border-[#F5D97A] rounded-lg p-2.5 text-center" data-testid="idle-banner">
              <p className="text-[11px] font-bold text-[#7F6632] uppercase tracking-wider">No active activity</p>
              <p className="text-[10px] text-gray-600 mt-0.5">Select one above and click Start to begin.</p>
            </div>
            <button
              onClick={() => act("/change-activity", { activity_type: selectedActivity, comment })}
              disabled={!selectedActivity || (selectedActivity === "Extra Activity" && comment.trim().length < 5)}
              className="w-full py-2.5 bg-[#A7C4A0] hover:bg-[#8FB388] text-white text-sm font-bold rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-40"
              data-testid="office-btn-start-activity"
            >
              <Play className="h-4 w-4" /> Start {selectedActivity}
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => act("/lunch-out")} className="py-2 bg-[#F5B971] hover:bg-[#E6A85C] text-[#1F2937] text-xs font-bold rounded-lg flex items-center justify-center gap-1" data-testid="office-btn-lunch-out-idle">
                <Coffee className="h-3.5 w-3.5" /> Lunch Out
              </button>
              <button
                onClick={() => {
                  if (state?.user?.requires_eod_summary && state?.day_has_extra_activity) { setEodText(""); setEodOpen(true); }
                  else if (window.confirm("Clock out for the day?")) act("/clock-out");
                }}
                className="py-2 bg-[#8B4545] hover:bg-[#6f3636] text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1"
                data-testid="office-btn-clock-out-idle"
              >
                <LogOut className="h-3.5 w-3.5" /> Clock Out
              </button>
            </div>
          </>
        )}

        {isClockedIn && isOnLunch && (
          <button onClick={() => act("/lunch-return", { activity_type: selectedActivity, comment })} className="w-full py-2.5 bg-[#A7C4A0] hover:bg-[#8FB388] text-white text-sm font-bold rounded-lg flex items-center justify-center gap-1.5" data-testid="office-btn-lunch-return">
            <Play className="h-4 w-4" /> Lunch Return → {selectedActivity}
          </button>
        )}

        {isClockedIn && isOnPause && (
          <button onClick={() => act("/resume", { activity_type: selectedActivity, comment })} className="w-full py-2.5 bg-[#A7C4A0] hover:bg-[#8FB388] text-white text-sm font-bold rounded-lg flex items-center justify-center gap-1.5" data-testid="office-btn-resume">
            <Play className="h-4 w-4" /> Resume → {selectedActivity}
          </button>
        )}

        {clockOut && (
          <div className="bg-gradient-to-r from-[#E8F0E4] to-[#F0F5EE] border border-[#A7C4A0] rounded-lg p-3" data-testid="office-clocked-out-banner">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] text-[#4A7A56] uppercase tracking-wider font-bold">⏹ Session Closed · Timers Stopped</p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Last clock-out: {new Date(clockOut).toLocaleTimeString()}
                  {(state?.time_log?.sessions?.length || 0) > 0 && <> · <b>{(state.time_log.sessions.length + 1)} sessions today</b></>}
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5">You can start a new session whenever you're ready — select an activity and Clock In again.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* End-of-Day Summary modal (Ana / Vitória / office-task users) */}
      {eodOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !eodSubmitting && setEodOpen(false)}>
          <div className="bg-white rounded-xl border border-gray-200 p-5 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="eod-summary-modal">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="text-base font-bold text-gray-800">End-of-Day Summary</h2>
                <p className="text-[11px] text-gray-500 mt-0.5">Required before clocking out</p>
              </div>
              <button onClick={() => !eodSubmitting && setEodOpen(false)} className="p-1 hover:bg-gray-100 rounded" data-testid="eod-close-btn">
                <X className="h-4 w-4 text-gray-400" />
              </button>
            </div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              What was the result of today's activity? <span className="text-[#C07070]">*</span>
            </label>
            <p className="text-[11px] text-gray-500 mb-2">
              Type what you completed today. e.g. "added client account information", "updated inspector accounts", "processed safeguard emails"…
            </p>
            <textarea
              value={eodText}
              onChange={e => setEodText(e.target.value)}
              rows={6}
              maxLength={1500}
              placeholder="Describe what you accomplished today…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#9A7A3E]/30"
              data-testid="eod-summary-text"
              disabled={eodSubmitting}
              autoFocus
            />
            <div className="flex items-center justify-between mt-1">
              <span className="text-[10px] text-gray-400">{eodText.trim().length} / 1500</span>
            </div>
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => !eodSubmitting && setEodOpen(false)}
                disabled={eodSubmitting}
                className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium rounded-lg disabled:opacity-40"
                data-testid="eod-cancel-btn"
              >
                Cancel
              </button>
              <button
                disabled={eodSubmitting || eodText.trim().length < 5}
                onClick={async () => {
                  setEodSubmitting(true);
                  try {
                    await act("/clock-out", { eod_summary: eodText.trim() });
                    setEodOpen(false);
                    setEodText("");
                  } finally {
                    setEodSubmitting(false);
                  }
                }}
                className="flex-1 py-2 bg-[#8B4545] hover:bg-[#6f3636] text-white text-sm font-bold rounded-lg disabled:opacity-40 flex items-center justify-center gap-1.5"
                data-testid="eod-submit-btn"
              >
                <LogOut className="h-3.5 w-3.5" />
                {eodSubmitting ? "Clocking out…" : "Submit & Clock Out"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════ HISTORY ══════════════════════════════ */
function HistoryPanel({ targetUserId = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const todayIso = new Date().toISOString().slice(0, 10);
  const monthAgoIso = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [start, setStart] = useState(monthAgoIso);
  const [end, setEnd] = useState(todayIso);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start, end };
      if (targetUserId) params.user_id = targetUserId;
      const r = await axios.get(`${API}/office/history`, { params });
      setData(r.data);
    } finally { setLoading(false); }
  }, [start, end, targetUserId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="office-history-panel">
      <div className="flex items-center gap-2 mb-4">
        <input type="date" value={start} onChange={e => setStart(e.target.value)} className="px-2 py-1 border border-gray-300 rounded text-xs" data-testid="history-start-date" />
        <span className="text-xs text-gray-400">to</span>
        <input type="date" value={end} onChange={e => setEnd(e.target.value)} className="px-2 py-1 border border-gray-300 rounded text-xs" data-testid="history-end-date" />
        <button onClick={fetchData} className="px-3 py-1 bg-[#2F5D8A] text-white text-xs font-semibold rounded">Refresh</button>
      </div>
      {loading ? <div className="py-8 text-center text-gray-400 text-xs">Loading...</div> : (
        <div className="space-y-3">
          {(data?.days || []).length === 0 && <div className="text-center py-8 text-xs text-gray-400">No activity in this period.</div>}
          {(data?.days || []).map(d => (
            <div key={d.work_date} className="border border-gray-100 rounded-lg p-3" data-testid={`history-day-${d.work_date}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-sm text-[#1F2937]">{d.work_date}</h3>
                <div className="flex gap-3 text-[10px]">
                  <span>Login: <b>{fmtMin(d.total_login_minutes)}</b></span>
                  <span>Paid: <b className="text-[#D1A054]">{fmtMin(d.total_paid_minutes)}</b></span>
                  <span>Lunch: <b>{fmtMin(d.total_lunch_minutes)}</b></span>
                  <span>Pause: <b>{fmtMin(d.total_pause_minutes)}</b></span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-[10px] text-gray-500 mb-2">
                <span>Clock In: <b className="text-gray-700">{d.clock_in ? new Date(d.clock_in).toLocaleTimeString() : "—"}</b></span>
                <span>Lunch: {d.lunch_out ? new Date(d.lunch_out).toLocaleTimeString() : "—"} → {d.lunch_return ? new Date(d.lunch_return).toLocaleTimeString() : "—"}</span>
                <span>Clock Out: <b className="text-gray-700">{d.clock_out ? new Date(d.clock_out).toLocaleTimeString() : "—"}</b></span>
              </div>
              {d.activities.length > 0 && (
                <div className="border-t border-gray-100 pt-2 space-y-1">
                  {d.activities.map((a, i) => (
                    <div key={i} className="flex items-center justify-between text-[10px]">
                      <div className="flex items-center gap-2">
                        <span className="px-1.5 py-0.5 rounded text-white text-[9px] font-bold" style={{ backgroundColor: STATUS_COLORS[a.status_type]?.bg || "#9CA3AF", color: STATUS_COLORS[a.status_type]?.text || "#fff" }}>
                          {a.status_type}
                        </span>
                        <span className="text-gray-700 font-semibold">{a.activity_type || "—"}</span>
                        {a.comment && <span className="text-gray-400 italic">"{a.comment}"</span>}
                      </div>
                      <span className="text-gray-500 font-mono">
                        {a.start_time ? new Date(a.start_time).toLocaleTimeString() : ""} → {a.end_time ? new Date(a.end_time).toLocaleTimeString() : "now"} ({fmtMin(a.duration_minutes)})
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {d.eod_summary && (
                <div className="border-t border-gray-100 pt-2 mt-2 bg-[#FFF8EC] -mx-3 -mb-3 px-3 py-2 rounded-b-lg" data-testid={`history-eod-${d.work_date}`}>
                  <p className="text-[9px] font-bold text-[#7F6632] uppercase tracking-wider mb-0.5">End-of-Day Summary</p>
                  <p className="text-[11px] text-gray-700 whitespace-pre-wrap leading-snug">{d.eod_summary}</p>
                  {d.eod_activity_totals && Object.keys(d.eod_activity_totals).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {Object.entries(d.eod_activity_totals).map(([k, v]) => (
                        <span key={k} className="px-1.5 py-0.5 bg-white border border-[#9A7A3E]/20 rounded text-[9px] text-[#7F6632] font-mono">
                          {k}: {fmtMin(v)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════ ADMIN: LIVE DASHBOARD ══════════════════════════════ */
function AdminDashboardPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(Date.now());

  const fetchData = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/office/live-dashboard`);
      setData(r.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 15000); return () => clearInterval(i); }, [fetchData]);
  useEffect(() => { const t = setInterval(() => setTick(Date.now()), 1000); return () => clearInterval(t); }, []);

  if (loading) return <div className="text-center py-8 text-gray-400">Loading...</div>;

  const emps = data?.employees || [];
  const stats = {
    online: emps.filter(e => e.status !== "Logged Out").length,
    lunch: emps.filter(e => e.status === "On Lunch").length,
    pause: emps.filter(e => e.status === "On Pause").length,
    active: emps.filter(e => e.status === "Active").length,
    totalLunchMin: emps.reduce((s, e) => s + (e.totals?.lunch || 0), 0),
    totalPaidMin: emps.reduce((s, e) => s + (e.totals?.paid || 0), 0),
  };

  return (
    <div className="space-y-4" data-testid="office-admin-dashboard">
      <div className="grid grid-cols-6 gap-3">
        {[
          { label: "Online", val: stats.online, color: "#2F5D8A", isNum: true },
          { label: "Active", val: stats.active, color: "#A7C4A0", isNum: true },
          { label: "On Lunch Now", val: stats.lunch, color: "#F5B971", isNum: true, testId: "on-lunch-count" },
          { label: "On Pause", val: stats.pause, color: "#D1A054", isNum: true },
          { label: "Unpaid Lunch Today", val: fmtMin(stats.totalLunchMin), color: "#9A6B2F", isNum: false, testId: "unpaid-lunch-today" },
          { label: "Billable Today", val: fmtMin(stats.totalPaidMin), color: "#2F7D32", isNum: false, testId: "billable-today" },
        ].map(s => (
          <div key={s.label} className="bg-white border border-gray-200 rounded-xl p-3 text-center" data-testid={s.testId}>
            <p className="text-[9px] text-gray-400 uppercase tracking-wider leading-tight">{s.label}</p>
            <p className={s.isNum ? "text-3xl font-bold" : "text-xl font-bold"} style={{ color: s.color }}>{s.val}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Employee</th>
              <th className="text-left px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Status</th>
              <th className="text-left px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Activity</th>
              <th className="text-left px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">In Status</th>
              <th className="text-left px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Lunch Today<br/><span className="text-[8px] text-gray-400 font-normal">(unpaid)</span></th>
              <th className="text-left px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Billable Today</th>
              <th className="text-left px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Week Days</th>
              <th className="text-left px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Week Billable</th>
            </tr>
          </thead>
          <tbody>
            {emps.map(e => {
              const badge = STATUS_COLORS[e.status] || STATUS_COLORS["Logged Out"];
              const onLunchNow = e.status === "On Lunch";
              return (
                <tr key={e.user_id} className={`border-t border-gray-100 ${onLunchNow ? "bg-amber-50" : ""}`} data-testid={`dashboard-row-${e.email}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-[#1F2937]">{e.name}</div>
                    <div className="text-[9px] text-gray-400">Office Team</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="px-2 py-0.5 rounded text-[9px] font-bold" style={{ backgroundColor: badge.bg, color: badge.text }}>{badge.label}</span>
                  </td>
                  <td className="px-3 py-2 text-gray-700">{e.activity_type || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[#2F5D8A]">{e.in_status_since ? (tick && fmtDuration(e.in_status_since)) : "—"}</td>
                  <td className="px-3 py-2 text-[#9A6B2F] font-semibold">{fmtMin(e.totals.lunch)}</td>
                  <td className="px-3 py-2 font-semibold text-[#2F7D32]">{fmtMin(e.totals.paid)}</td>
                  <td className="px-3 py-2">{e.weekly?.days_worked || 0}d</td>
                  <td className="px-3 py-2 font-semibold text-[#2F7D32]">{fmtMin(e.weekly?.paid_min || 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ══════════════════════════════ ADMIN: PAYROLL ══════════════════════════════ */
function PayrollPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const today = new Date();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [editRow, setEditRow] = useState(null);
  const [editAdj, setEditAdj] = useState(0);
  const [editNote, setEditNote] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      const r = await axios.get(`${API}/office/payroll`, { params });
      setData(r.data);
    } finally { setLoading(false); }
  }, [start, end]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const currentPeriodBtn = (idx) => () => {
    const d = today;
    let s, e;
    if (idx === 0) { s = new Date(d.getFullYear(), d.getMonth(), 1); e = new Date(d.getFullYear(), d.getMonth(), 15); }
    else { s = new Date(d.getFullYear(), d.getMonth(), 16); e = new Date(d.getFullYear(), d.getMonth() + 1, 0); }
    setStart(s.toISOString().slice(0, 10));
    setEnd(e.toISOString().slice(0, 10));
  };

  const saveAdjustment = async () => {
    try {
      await axios.post(`${API}/office/payroll/adjustment`, {
        employee_id: editRow.employee_id,
        pay_period_start: data.pay_period_start,
        pay_period_end: data.pay_period_end,
        adjustments: parseFloat(editAdj) || 0,
        note: editNote,
      });
      setEditRow(null);
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || "Failed to save"); }
  };

  return (
    <div className="space-y-4" data-testid="office-payroll-panel">
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={currentPeriodBtn(0)} className="px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-xs rounded" data-testid="payroll-period-1-15">Day 1-15</button>
          <button onClick={currentPeriodBtn(1)} className="px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-xs rounded" data-testid="payroll-period-16-end">Day 16-end</button>
          <span className="text-xs text-gray-400 mx-2">or pick range:</span>
          <input type="date" value={start} onChange={e => setStart(e.target.value)} className="px-2 py-1 border border-gray-300 rounded text-xs" />
          <span className="text-xs text-gray-400">to</span>
          <input type="date" value={end} onChange={e => setEnd(e.target.value)} className="px-2 py-1 border border-gray-300 rounded text-xs" />
          <button onClick={fetchData} className="px-3 py-1 bg-[#2F5D8A] text-white text-xs font-semibold rounded">Compute</button>
        </div>
        {data && (
          <p className="text-xs text-gray-500 mt-2">
            Pay period: <b>{data.pay_period_start}</b> to <b>{data.pay_period_end}</b>
          </p>
        )}
      </div>

      {loading ? <div className="text-center py-8 text-gray-400">Loading...</div> : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full text-xs min-w-[950px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Employee</th>
                <th className="text-left px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Type</th>
                <th className="text-right px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Days</th>
                <th className="text-right px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Login h</th>
                <th className="text-right px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Work h</th>
                <th className="text-right px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Lunch h</th>
                <th className="text-right px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Pause h</th>
                <th className="text-right px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Paid h</th>
                <th className="text-right px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Gross</th>
                <th className="text-right px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Adj.</th>
                <th className="text-right px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Final</th>
                <th className="text-center px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Edit</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map(r => (
                <tr key={r.employee_id} className="border-t border-gray-100" data-testid={`payroll-row-${r.email}`}>
                  <td className="px-3 py-2 font-semibold text-[#1F2937]">{r.name}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${r.payment_type === "salary" ? "bg-[#2F5D8A]/15 text-[#2F5D8A]" : "bg-[#A7C4A0]/25 text-[#4a7a42]"}`}>
                      {r.payment_type}
                    </span>
                    {r.payment_type === "salary" && <div className="text-[9px] text-gray-400">R${r.monthly_salary}/mo</div>}
                    {r.payment_type === "hourly" && <div className="text-[9px] text-gray-400">R${r.hourly_rate}/h</div>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {r.days_worked}
                    {r.prorate_note && <div className="text-[9px] text-gray-400">{r.prorate_note}</div>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{r.login_hours.toFixed(1)}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.work_hours.toFixed(1)}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#F5B971]">{r.lunch_hours.toFixed(1)}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#D1A054]">{r.pause_hours.toFixed(1)}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold">{r.paid_hours.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono">R${r.gross_pay.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono">R${r.adjustments.toFixed(2)}
                    {r.adjustment_note && <div className="text-[9px] text-gray-400 italic truncate max-w-[100px]" title={r.adjustment_note}>{r.adjustment_note}</div>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-[#2F5D8A]" data-testid={`payroll-final-${r.email}`}>R${r.final_pay.toFixed(2)}</td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => { setEditRow(r); setEditAdj(r.adjustments); setEditNote(r.adjustment_note || ""); }} className="text-[#2F5D8A] hover:text-[#24496e]" data-testid={`payroll-edit-${r.email}`}>
                      <Edit3 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editRow && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1100] p-4" onClick={() => setEditRow(null)}>
          <div className="bg-white rounded-xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-[#1F2937]">Payroll Adjustment — {editRow.name}</h3>
            <p className="text-xs text-gray-500">Period: {data.pay_period_start} to {data.pay_period_end}</p>
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">Adjustment amount (R$)</label>
              <input type="number" step="0.01" value={editAdj} onChange={e => setEditAdj(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded text-sm" data-testid="payroll-adj-amount" />
              <p className="text-[10px] text-gray-400 mt-0.5">Use negative to deduct, positive to add.</p>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">Note</label>
              <input type="text" value={editNote} onChange={e => setEditNote(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded text-sm" data-testid="payroll-adj-note" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditRow(null)} className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-sm font-semibold rounded">Cancel</button>
              <button onClick={saveAdjustment} className="flex-1 py-2 bg-[#2F5D8A] hover:bg-[#24496e] text-white text-sm font-bold rounded" data-testid="payroll-adj-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════ LIVE EARNINGS TICKER ══════════════════════════════ */
function EarningsLiveTicker({ state, nowTick }) {
  // Compute live billable minutes = base paid minutes + live elapsed if currently Active/Pause (pause capped 30m total)
  const rate = state?.user?.hourly_effective_rate || 0; // R$ per hour
  const basePaid = state?.totals?.total_paid_minutes || 0;
  const basePause = state?.totals?.total_pause_minutes || 0;
  const PAUSE_CAP = 30;
  const segStart = state?.current_segment?.start_time;
  const segStatus = state?.current_segment?.status_type;
  const segActivity = state?.current_segment?.activity_type;
  const clockOut = state?.time_log?.clock_out;
  const clockIn = state?.time_log?.clock_in;
  // Hard lock: logged out OR currently in a non-billable activity (Transmission).
  const isLoggedOut = !clockIn || !!clockOut || state?.status === "Logged Out";
  const isTransmission = !isLoggedOut && NON_BILLABLE_ACTIVITIES.has(segActivity);

  let livePaidMin = basePaid;
  let isPaused = false;
  if (!isLoggedOut && !isTransmission && segStart) {
    const elapsed = Math.max(0, (nowTick - new Date(segStart).getTime()) / 60000);
    if (segStatus === "Active") {
      livePaidMin = basePaid + elapsed;
    } else if (segStatus === "On Pause") {
      const remainingPaidPause = Math.max(0, PAUSE_CAP - basePause);
      livePaidMin = basePaid + Math.min(elapsed, remainingPaidPause);
      if (elapsed >= remainingPaidPause) isPaused = true;
    } else if (segStatus === "On Lunch") {
      isPaused = true;
    }
  }

  const earnings = (livePaidMin / 60) * rate;
  const ratePretty = rate > 0 ? `R$${rate.toFixed(2)}/h` : "—";
  const isOnLunch = !isLoggedOut && segStatus === "On Lunch";

  return (
    <div className={`rounded-lg p-3 border-2 transition-colors ${
      isTransmission ? "bg-[#F3E8F5] border-[#B07CC4]"
      : isLoggedOut ? "bg-gray-50 border-gray-200"
      : isPaused ? "bg-amber-50 border-amber-300"
      : "bg-gradient-to-r from-[#F5F9FF] to-[#EBF5F0] border-[#A7C4A0]"
    }`} data-testid="earnings-ticker">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1 flex-wrap">
            <TrendingUp className="h-3 w-3" /> Estimated Earnings Today
            {isTransmission && <span className="ml-1 px-1.5 py-0.5 bg-[#B07CC4] text-white rounded text-[9px] font-bold" data-testid="earnings-transmission-mode">TRANSMISSION (PAID PER ORDER)</span>}
            {isLoggedOut && !isTransmission && <span className="ml-1 px-1.5 py-0.5 bg-gray-300 text-gray-700 rounded text-[9px] font-bold" data-testid="earnings-frozen-loggedout">FROZEN (LOGGED OUT)</span>}
            {!isLoggedOut && !isTransmission && isOnLunch && <span className="ml-1 px-1.5 py-0.5 bg-amber-200 text-amber-900 rounded text-[9px] font-bold" data-testid="earnings-paused-lunch">PAUSED (LUNCH)</span>}
            {!isLoggedOut && !isTransmission && isPaused && !isOnLunch && <span className="ml-1 px-1.5 py-0.5 bg-gray-200 text-gray-700 rounded text-[9px] font-bold">PAUSED</span>}
          </p>
          <p className={`text-2xl font-bold mt-0.5 ${isTransmission ? "text-[#7B4D8E]" : isLoggedOut ? "text-gray-500" : "text-[#2F5D8A]"}`} data-testid="earnings-amount">
            R$ {earnings.toFixed(2)}
          </p>
          <p className="text-[9px] text-gray-400 mt-0.5">
            {fmtMin(livePaidMin)} billable · rate {ratePretty}
            {isTransmission && <> · <b className="text-[#7B4D8E]">not counting now</b></>}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[9px] text-gray-400 uppercase">Lunch (unpaid)</p>
          <p className="text-sm font-semibold text-[#9A6B2F]" data-testid="earnings-lunch-min">{fmtMin(state?.totals?.total_lunch_minutes || 0)}</p>
        </div>
      </div>
    </div>
  );
}



/* ══════════════════════════════ PRODUCTIVITY LIVE (employee) ══════════════════════════════ */
const STATUS_BADGE = {
  "On Target": { bg: "#A7C4A0", color: "#1F4427", label: "On Target" },
  "Catching Up": { bg: "#F5B971", color: "#5C3A00", label: "Catching Up" },
  "Warming Up": { bg: "#E5E7EB", color: "#4B5563", label: "Warming Up" },
  "No Target": { bg: "#E5E7EB", color: "#4B5563", label: "No Target" },
  "Needs Justification": { bg: "#DC2626", color: "#FFFFFF", label: "Needs Justification" },
  "Low (Justified)": { bg: "#D1A054", color: "#FFFFFF", label: "Low (Justified)" },
};
const MANUAL_ACTIVITIES = ["Client Email", "Safeguard Email", "Inspector Support"];

function ProductivityLiveCard({ activityType, onMsg }) {
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showJustify, setShowJustify] = useState(false);

  const fetchLive = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/office/productivity/my-live`);
      setLive(r.data?.live);
    } catch {}
  }, []);

  useEffect(() => { fetchLive(); const i = setInterval(fetchLive, 20000); return () => clearInterval(i); }, [fetchLive, activityType]);

  const logOne = async () => {
    try {
      setLoading(true);
      const r = await axios.post(`${API}/office/productivity/log-event`, { activity_type: activityType });
      setLive(r.data?.live);
      if (onMsg) onMsg({ type: "success", text: `+1 ${activityType} logged` });
    } catch (e) {
      if (onMsg) onMsg({ type: "error", text: e.response?.data?.detail || "Failed to log" });
    } finally { setLoading(false); }
  };

  if (!live) return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
      <p className="text-[10px] text-gray-400 uppercase tracking-wider">Productivity</p>
      <p className="text-xs text-gray-400">Waiting for data...</p>
    </div>
  );

  const isManual = MANUAL_ACTIVITIES.includes(activityType);
  const badge = STATUS_BADGE[live.status] || STATUS_BADGE["Warming Up"];
  const pct = live.ratio_pct !== null ? Math.min(120, Math.max(0, live.ratio_pct)) : 0;
  const barColor = live.is_alert ? "#DC2626" : (pct >= 100 ? "#A7C4A0" : pct >= 50 ? "#F5B971" : "#D1A054");

  return (
    <div className="bg-white border-2 rounded-lg p-3 space-y-2" style={{ borderColor: live.is_alert ? "#DC2626" : "#E5E7EB" }} data-testid="productivity-live-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Target className="h-3.5 w-3.5 text-[#2F5D8A]" />
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Productivity — {activityType}</p>
        </div>
        <span className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ backgroundColor: badge.bg, color: badge.color }} data-testid="productivity-status-badge">{badge.label}</span>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="text-[9px] text-gray-400 uppercase">Produced</p>
          <p className="text-xl font-bold text-[#1F2937]" data-testid="productivity-produced">{live.produced_units}</p>
        </div>
        <div>
          <p className="text-[9px] text-gray-400 uppercase">Expected</p>
          <p className="text-xl font-bold text-[#9CA3AF]">{live.expected_units}</p>
        </div>
        <div>
          <p className="text-[9px] text-gray-400 uppercase">Target/h</p>
          <p className="text-xl font-bold text-[#2F5D8A]">{live.target_per_hour}</p>
        </div>
      </div>

      <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
        <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: barColor }} data-testid="productivity-bar" />
      </div>
      <p className="text-[9px] text-gray-500 text-center">
        {live.minutes_on_activity}min on activity · {live.source === "ringcentral" ? "auto (RingCentral)" : "manual log"}
      </p>

      {isManual && (
        <button
          onClick={logOne}
          disabled={loading}
          className="w-full py-2 bg-[#A7C4A0] hover:bg-[#8FB388] disabled:opacity-50 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1.5"
          data-testid="productivity-log-btn"
        >
          <Zap className="h-3.5 w-3.5" /> +1 {activityType} Done
        </button>
      )}

      {live.is_alert && !live.justification && (
        <button
          onClick={() => setShowJustify(true)}
          className="w-full py-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1.5"
          data-testid="productivity-justify-btn"
        >
          <Flag className="h-3.5 w-3.5" /> Justify Low Production
        </button>
      )}

      {live.justification && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2">
          <p className="text-[10px] font-bold text-amber-800">Justification submitted</p>
          <p className="text-[10px] text-amber-700 italic">"{live.justification.text}"</p>
          {live.justification.reviewed_ok === true && <p className="text-[10px] text-green-700 mt-1">Approved by admin</p>}
          {live.justification.reviewed_ok === false && <p className="text-[10px] text-red-700 mt-1">Rejected by admin</p>}
        </div>
      )}

      {showJustify && (
        <JustificationModal
          activityType={activityType}
          onClose={() => setShowJustify(false)}
          onSuccess={() => { setShowJustify(false); fetchLive(); if (onMsg) onMsg({ type: "success", text: "Justification submitted" }); }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════ JUSTIFICATION MODAL ══════════════════════════════ */
const JUSTIFY_REASONS = [
  { val: "helping_admin", label: "Helping with admin task" },
  { val: "system_problem", label: "System problem" },
  { val: "inspector_support", label: "Inspector support" },
  { val: "urgent_issue", label: "Urgent issue" },
  { val: "special_task", label: "Special task assigned by management" },
  { val: "other", label: "Other (explain)" },
];

function JustificationModal({ activityType, onClose, onSuccess }) {
  const [reason, setReason] = useState("helping_admin");
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (text.trim().length < 5) { setErr("Please write at least 5 characters."); return; }
    try {
      setSubmitting(true);
      setErr(null);
      await axios.post(`${API}/office/productivity/justify`, {
        activity_type: activityType, reason, text: text.trim(),
      });
      onSuccess();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1100] p-4" onClick={onClose} data-testid="justify-modal">
      <div className="bg-white rounded-xl w-full max-w-lg p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded p-2.5">
          <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-red-800">Low Production Alert — {activityType}</p>
            <p className="text-[10px] text-red-700">Your output is below target. Management requires a justification before this gets flagged in payroll review.</p>
          </div>
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-600 mb-1">Reason</label>
          <select value={reason} onChange={e => setReason(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded text-sm" data-testid="justify-reason">
            {JUSTIFY_REASONS.map(r => <option key={r.val} value={r.val}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-600 mb-1">Explain what happened ({text.length}/5 min)</label>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={4} maxLength={1000} className="w-full px-3 py-2 border border-gray-300 rounded text-sm" placeholder="e.g., I was helping Aline upload the morning PDFs..." data-testid="justify-text" />
        </div>
        {err && <p className="text-xs text-red-600" data-testid="justify-error">{err}</p>}
        <div className="flex gap-2">
          <button onClick={onClose} disabled={submitting} className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-sm font-semibold rounded" data-testid="justify-cancel">Cancel</button>
          <button onClick={submit} disabled={submitting || text.trim().length < 5} className="flex-1 py-2 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white text-sm font-bold rounded" data-testid="justify-submit">
            {submitting ? "Submitting..." : "Submit Justification"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════ ADMIN: PRODUCTIVITY DASHBOARD ══════════════════════════════ */
function LiveQueueCountersCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showThresholds, setShowThresholds] = useState(false);
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem("aarifi_queue_alerts_muted") === "1"; } catch { return false; }
  });
  const lastAlertKeysRef = useRef(new Set());

  const fetchCounters = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/office/queue-counters`);
      setData(r.data); setErr(null);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load");
    }
  }, []);
  useEffect(() => {
    fetchCounters();
    const i = setInterval(fetchCounters, 15000);
    return () => clearInterval(i);
  }, [fetchCounters]);

  // Recipients: only Aline + Angela get the audible beep.
  const isRecipient = !!data?.alert_recipients?.includes((user?.email || "").toLowerCase());

  // Play a short beep when a NEW queue enters alert state (transitions false→true).
  useEffect(() => {
    if (!data || !isRecipient || muted) { lastAlertKeysRef.current = new Set(data?.active_alerts || []); return; }
    const current = new Set(data.active_alerts || []);
    const prev = lastAlertKeysRef.current;
    const newly = [...current].filter(k => !prev.has(k));
    if (newly.length > 0) {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) {
          const ctx = new Ctx();
          [0, 0.25, 0.5].forEach(delay => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.frequency.value = 880;
            osc.type = "sine";
            gain.gain.setValueAtTime(0.0001, ctx.currentTime + delay);
            gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + delay + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + 0.18);
            osc.connect(gain); gain.connect(ctx.destination);
            osc.start(ctx.currentTime + delay);
            osc.stop(ctx.currentTime + delay + 0.2);
          });
          setTimeout(() => { try { ctx.close(); } catch {} }, 1200);
        }
      } catch { /* audio blocked / unsupported — silent fallback */ }
    }
    lastAlertKeysRef.current = current;
  }, [data, isRecipient, muted]);

  const toggleMute = () => {
    const nv = !muted;
    setMuted(nv);
    try { localStorage.setItem("aarifi_queue_alerts_muted", nv ? "1" : "0"); } catch { /* ignore */ }
  };

  if (err) return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 text-xs text-red-600">Queue counters: {err}</div>
  );
  if (!data) return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 text-xs text-gray-400">Loading live queues…</div>
  );

  const Q = data.queues || {};
  const cards = [
    { key: "safeguard_calls_today", label: "Safeguard Calls · Today",  tone: "#2F5D8A", tint: "#EAF1F9", icon: "SG" },
    { key: "external_calls_today",  label: "External Calls · Today",   tone: "#8A5A1F", tint: "#FAE8C6", icon: "EX" },
    { key: "national_calls_today",  label: "National Calls · Today",   tone: "#7B4D8E", tint: "#F3E8F5", icon: "NT" },
    { key: "priority_queue",        label: "Priority Queue",           tone: "#DC2626", tint: "#FEE2E2", icon: "PR" },
  ];
  const baselineStr = data.baseline_captured_at
    ? new Date(data.baseline_captured_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
    : "—";
  const alertKeys = new Set(data.active_alerts || []);
  const hasAlert = alertKeys.size > 0;

  return (
    <>
      <style>{`
        @keyframes qAlertPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.55); } 50% { box-shadow: 0 0 0 6px rgba(220,38,38,0.0); } }
        .queue-alert-pulse { animation: qAlertPulse 1.4s ease-in-out infinite; border-color: #DC2626 !important; }
      `}</style>
      <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="live-queue-counters">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-bold text-[#1F2937] flex items-center gap-1.5">
              <TrendingUp className="h-4 w-4 text-[#2F5D8A]" />
              Live Call Queues · Decreasing as work is completed
            </h3>
            <p className="text-[10px] text-gray-500 mt-0.5">Baseline captured at {baselineStr} · refreshed every 15s</p>
          </div>
          <div className="flex items-center gap-2">
            {isRecipient && (
              <button
                type="button"
                onClick={toggleMute}
                className={`px-2.5 py-1.5 rounded text-[11px] font-semibold border transition-colors ${muted ? "bg-gray-100 text-gray-500 border-gray-300" : "bg-white text-[#2F5D8A] border-[#2F5D8A]/40 hover:bg-[#2F5D8A]/5"}`}
                data-testid="queue-alerts-mute-btn"
                title={muted ? "Alerts muted — click to un-mute" : "Alerts on — click to mute beep"}
              >
                {muted ? "🔕 Muted" : "🔔 Alerts On"}
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowThresholds(true)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-[11px] font-semibold rounded"
              data-testid="queue-thresholds-btn"
            >
              <Settings className="h-3.5 w-3.5" /> Thresholds
            </button>
          </div>
        </div>

        {hasAlert && (
          <div className="mb-3 flex items-center gap-2 p-2.5 bg-red-50 border-l-4 border-red-500 rounded-r" data-testid="queue-alert-banner">
            <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
            <p className="text-[11px] text-red-800">
              <span className="font-bold">{alertKeys.size} queue{alertKeys.size > 1 ? "s" : ""}</span> past threshold — reinforce the team.
              {isRecipient && !muted && <span className="ml-1 text-[10px] text-red-600">(audible beep enabled for you)</span>}
            </p>
          </div>
        )}

        <div className="grid grid-cols-4 gap-3">
          {cards.map(c => {
            const q = Q[c.key] || { started: 0, done: 0, remaining: 0, pct: 0, alert: {} };
            const pct = Math.min(100, Math.max(0, q.pct || 0));
            const alert = q.alert || {};
            const inAlert = !!alert.is_alert;
            const borderClass = inAlert ? "queue-alert-pulse border-2" : "border border-gray-200";
            return (
              <div key={c.key} className={`rounded-lg p-3 ${borderClass}`} style={{ backgroundColor: c.tint }} data-testid={`queue-card-${c.key}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: c.tone }}>{c.label}</span>
                  {inAlert ? (
                    <span className="px-1.5 py-0.5 bg-red-600 text-white rounded text-[9px] font-bold animate-pulse" data-testid={`queue-alert-badge-${c.key}`}>ALERT</span>
                  ) : (
                    <span className="px-1.5 py-0.5 bg-white/70 rounded text-[9px] font-bold" style={{ color: c.tone }}>{c.icon}</span>
                  )}
                </div>
                <div className="flex items-end justify-between">
                  <p className="text-4xl font-bold leading-none" style={{ color: inAlert ? "#DC2626" : c.tone }} data-testid={`queue-remaining-${c.key}`}>
                    {q.remaining}
                  </p>
                  <div className="text-right">
                    <p className="text-[9px] text-gray-600">Started <b>{q.started}</b></p>
                    <p className="text-[9px] text-gray-600">Done <b style={{ color: "#1F8A3C" }}>{q.done}</b></p>
                  </div>
                </div>
                <div className="mt-2 h-1.5 bg-white/70 rounded-full overflow-hidden">
                  <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: inAlert ? "#DC2626" : c.tone }} />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-[9px] text-gray-500">
                    {alert.threshold != null ? <>Alert if &gt; <b>{alert.threshold}</b>{alert.alert_after_hour > 0 ? <> after {alert.alert_after_hour}:00</> : null}</> : null}
                  </p>
                  <p className="text-[9px] text-gray-500">{pct}% completed</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showThresholds && (
        <QueueThresholdsModal
          onClose={() => setShowThresholds(false)}
          onSaved={() => { setShowThresholds(false); fetchCounters(); }}
        />
      )}
    </>
  );
}

function QueueThresholdsModal({ onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = ["Admin", "Finance"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/office/queue-alert-configs`);
        setRows(r.data?.configs || []);
      } catch (e) {
        setErr(e.response?.data?.detail || "Failed");
      } finally { setLoading(false); }
    })();
  }, []);

  const patch = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await axios.post(`${API}/office/queue-alert-configs`, {
        configs: rows.map(r => ({
          queue_key: r.queue_key,
          threshold: Number(r.threshold) || 0,
          alert_after_hour: Number(r.alert_after_hour) || 0,
          enabled: !!r.enabled,
        })),
      });
      if (onSaved) onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[1200] flex items-center justify-center p-4" data-testid="queue-thresholds-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-xl w-full p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-bold text-[#1F2937]">Queue Alert Thresholds</h3>
            <p className="text-[11px] text-gray-500">Visual + audible alert fires when remaining is above threshold past the configured hour (Boston). Recipients: Aline, Angela.</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="queue-thresholds-close"><X className="h-4 w-4" /></button>
        </div>

        {loading ? <p className="text-xs text-gray-400 py-6 text-center">Loading…</p> : (
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-2 py-1.5 text-[10px] font-bold text-gray-500 uppercase">Queue</th>
                <th className="text-right px-2 py-1.5 text-[10px] font-bold text-gray-500 uppercase">Alert if &gt;</th>
                <th className="text-right px-2 py-1.5 text-[10px] font-bold text-gray-500 uppercase">After hour</th>
                <th className="text-center px-2 py-1.5 text-[10px] font-bold text-gray-500 uppercase">Enabled</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.queue_key} className="border-b border-gray-100" data-testid={`threshold-row-${r.queue_key}`}>
                  <td className="px-2 py-1.5 font-semibold">{r.label || r.queue_key}</td>
                  <td className="px-2 py-1.5">
                    <input type="number" min="0" step="1" value={r.threshold}
                      onChange={e => patch(i, "threshold", e.target.value)}
                      disabled={!isAdmin}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-xs text-right disabled:bg-gray-50"
                      data-testid={`threshold-input-${r.queue_key}`} />
                  </td>
                  <td className="px-2 py-1.5">
                    <input type="number" min="0" max="23" step="1" value={r.alert_after_hour}
                      onChange={e => patch(i, "alert_after_hour", e.target.value)}
                      disabled={!isAdmin}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-xs text-right disabled:bg-gray-50"
                      data-testid={`threshold-hour-${r.queue_key}`} />
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <input type="checkbox" checked={!!r.enabled}
                      onChange={e => patch(i, "enabled", e.target.checked)}
                      disabled={!isAdmin}
                      data-testid={`threshold-enabled-${r.queue_key}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {err && <p className="text-xs text-red-600 mt-2">{err}</p>}

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 border border-gray-300 text-xs font-semibold rounded hover:bg-gray-50">Close</button>
          {isAdmin && (
            <button onClick={save} disabled={saving || loading} className="px-3 py-1.5 bg-[#2F5D8A] text-white text-xs font-semibold rounded hover:bg-[#24496b] disabled:opacity-50" data-testid="queue-thresholds-save">
              {saving ? "Saving…" : "Save thresholds"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function ProductivityPanel() {
  const [data, setData] = useState(null);
  const [targets, setTargets] = useState([]);
  const [showTargets, setShowTargets] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(Date.now());

  const fetchData = useCallback(async () => {
    try {
      const [d, t] = await Promise.all([
        axios.get(`${API}/office/productivity/dashboard`),
        axios.get(`${API}/office/productivity/targets`),
      ]);
      setData(d.data);
      setTargets(t.data?.targets || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 15000); return () => clearInterval(i); }, [fetchData]);
  useEffect(() => { const t = setInterval(() => setTick(Date.now()), 1000); return () => clearInterval(t); }, []);

  if (loading) return <div className="text-center py-8 text-gray-400">Loading productivity...</div>;

  const emps = data?.employees || [];
  const alerts = data?.active_alerts || [];

  return (
    <div className="space-y-4" data-testid="office-productivity-panel">
      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-[10px] text-gray-400 uppercase tracking-wider">Active Now</p>
          <p className="text-3xl font-bold text-[#2F5D8A]" data-testid="prod-active-count">{emps.filter(e => e.live).length}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-[10px] text-gray-400 uppercase tracking-wider">On Target</p>
          <p className="text-3xl font-bold text-[#A7C4A0]">{emps.filter(e => e.live?.status === "On Target").length}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-[10px] text-gray-400 uppercase tracking-wider">Need Justification</p>
          <p className="text-3xl font-bold text-[#DC2626]" data-testid="prod-alert-count">{alerts.length}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-[10px] text-gray-400 uppercase tracking-wider">Low (Justified)</p>
          <p className="text-3xl font-bold text-[#D1A054]">{emps.filter(e => e.live?.status === "Low (Justified)").length}</p>
        </div>
      </div>

      {/* Live queue counters — decrease as operators close work */}
      <LiveQueueCountersCard />

      {/* Active alerts banner */}
      {alerts.length > 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-3" data-testid="prod-alerts-banner">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <p className="text-sm font-bold text-red-800">{alerts.length} employee(s) need justification</p>
          </div>
          <div className="space-y-1">
            {alerts.map((a, i) => (
              <div key={i} className="flex items-center justify-between bg-white border border-red-200 rounded px-3 py-1.5 text-xs" data-testid={`prod-alert-${a.email}`}>
                <div className="flex-1">
                  <span className="font-bold text-[#1F2937]">{a.name}</span>
                  <span className="text-gray-500"> · {a.activity_type}</span>
                </div>
                <div className="text-[10px] text-gray-600">
                  {a.minutes_on_activity}min · <b>{a.produced_units}</b> / {a.expected_units} expected
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Edit targets button */}
      <div className="flex justify-end">
        <button onClick={() => setShowTargets(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-xs font-semibold rounded" data-testid="prod-edit-targets-btn">
          <Settings className="h-3.5 w-3.5" /> Edit Targets
        </button>
      </div>

      {/* Live table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Employee</th>
              <th className="text-left px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Activity</th>
              <th className="text-right px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Time In</th>
              <th className="text-right px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Target/h</th>
              <th className="text-right px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Expected</th>
              <th className="text-right px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Produced</th>
              <th className="text-center px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Status</th>
              <th className="text-left px-3 py-2 text-[10px] font-bold text-gray-500 uppercase">Justification</th>
            </tr>
          </thead>
          <tbody>
            {emps.map(e => {
              const l = e.live;
              const badge = l ? (STATUS_BADGE[l.status] || STATUS_BADGE["Warming Up"]) : null;
              return (
                <tr key={e.user_id} className={`border-t border-gray-100 ${l?.is_alert && !l?.justification ? "bg-red-50" : ""}`} data-testid={`prod-row-${e.email}`}>
                  <td className="px-3 py-2 font-semibold text-[#1F2937]">{e.name}</td>
                  <td className="px-3 py-2 text-gray-700">{e.current_activity || <span className="text-gray-400">—</span>}</td>
                  <td className="px-3 py-2 text-right font-mono">{l ? `${l.minutes_on_activity}m` : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#2F5D8A]">{l ? l.target_per_hour : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-gray-500">{l ? l.expected_units : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold">{l ? l.produced_units : "—"}</td>
                  <td className="px-3 py-2 text-center">
                    {badge && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ backgroundColor: badge.bg, color: badge.color }}>{badge.label}</span>}
                  </td>
                  <td className="px-3 py-2">
                    {l?.justification ? (
                      <JustificationReview j={l.justification} onReviewed={fetchData} />
                    ) : l?.is_alert ? (
                      <span className="text-[10px] text-red-600 font-semibold">awaiting employee</span>
                    ) : (
                      <span className="text-[10px] text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showTargets && <TargetsEditorModal targets={targets} onClose={() => setShowTargets(false)} onSaved={fetchData} />}
    </div>
  );
}

function JustificationReview({ j, onReviewed }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(j.admin_note || "");
  const act = async (ok) => {
    try {
      await axios.post(`${API}/office/productivity/review`, { justification_id: j.id, reviewed_ok: ok, admin_note: note });
      setOpen(false);
      if (onReviewed) onReviewed();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="text-[10px] text-[#2F5D8A] font-semibold hover:underline truncate max-w-[180px] text-left" data-testid="prod-justification-link">
        "{j.text.slice(0, 40)}{j.text.length > 40 ? "..." : ""}"
        {j.reviewed_ok === true && <span className="ml-1 text-green-600">✓</span>}
        {j.reviewed_ok === false && <span className="ml-1 text-red-600">✗</span>}
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1100] p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl w-full max-w-md p-4 space-y-3" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold">Review Justification</h3>
            <p className="text-[10px] text-gray-500">Reason: <b>{j.reason}</b></p>
            <div className="bg-gray-50 rounded p-2 text-xs italic text-gray-700">"{j.text}"</div>
            <div>
              <label className="block text-[10px] font-bold text-gray-600 mb-1">Admin note (optional)</label>
              <input type="text" value={note} onChange={e => setNote(e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => act(false)} className="flex-1 py-1.5 bg-red-100 hover:bg-red-200 text-red-800 text-xs font-bold rounded" data-testid="prod-justify-reject">Reject</button>
              <button onClick={() => act(true)} className="flex-1 py-1.5 bg-green-100 hover:bg-green-200 text-green-800 text-xs font-bold rounded" data-testid="prod-justify-approve">Approve</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TargetsEditorModal({ targets, onClose, onSaved }) {
  const [rows, setRows] = useState(() => targets.map(t => ({ ...t })));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    try {
      setSaving(true);
      for (const r of rows) {
        await axios.post(`${API}/office/productivity/targets`, {
          activity_type: r.activity_type,
          target_per_hour: parseFloat(r.target_per_hour) || 0,
          alert_min_minutes: parseInt(r.alert_min_minutes) || 60,
          threshold_ratio: parseFloat(r.threshold_ratio) || 0.5,
        });
      }
      onSaved();
      onClose();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const patch = (i, field, val) => setRows(rs => rs.map((r, j) => j === i ? { ...r, [field]: val } : r));

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1100] p-4" onClick={onClose} data-testid="targets-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl p-4 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <Settings className="h-4 w-4 text-[#2F5D8A]" />
          <h3 className="text-base font-bold">Productivity Targets</h3>
        </div>
        <p className="text-[10px] text-gray-500">Set target units per hour. Alert fires after min-minutes if produced &lt; threshold × expected.</p>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left px-2 py-1.5 text-[10px] font-bold text-gray-500 uppercase">Activity</th>
              <th className="text-right px-2 py-1.5 text-[10px] font-bold text-gray-500 uppercase">Target/h</th>
              <th className="text-right px-2 py-1.5 text-[10px] font-bold text-gray-500 uppercase">Alert after (min)</th>
              <th className="text-right px-2 py-1.5 text-[10px] font-bold text-gray-500 uppercase">Threshold (0-1)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.activity_type} className="border-b border-gray-100" data-testid={`target-row-${r.activity_type}`}>
                <td className="px-2 py-1.5 font-semibold">{r.activity_type}</td>
                <td className="px-2 py-1.5">
                  <input type="number" min="0" step="1" value={r.target_per_hour} onChange={e => patch(i, "target_per_hour", e.target.value)} className="w-20 px-2 py-1 border border-gray-300 rounded text-xs text-right" data-testid={`target-input-${r.activity_type}`} />
                </td>
                <td className="px-2 py-1.5">
                  <input type="number" min="5" step="5" value={r.alert_min_minutes} onChange={e => patch(i, "alert_min_minutes", e.target.value)} className="w-20 px-2 py-1 border border-gray-300 rounded text-xs text-right" />
                </td>
                <td className="px-2 py-1.5">
                  <input type="number" min="0.1" max="1" step="0.05" value={r.threshold_ratio} onChange={e => patch(i, "threshold_ratio", e.target.value)} className="w-20 px-2 py-1 border border-gray-300 rounded text-xs text-right" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-sm font-semibold rounded">Cancel</button>
          <button onClick={save} disabled={saving} className="flex-1 py-2 bg-[#2F5D8A] hover:bg-[#24496e] disabled:opacity-50 text-white text-sm font-bold rounded" data-testid="targets-save">
            {saving ? "Saving..." : "Save All"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════ OPENING SHIFT CARD (João, 8-9 AM Boston) ══════════════════════════════ */
function OpeningShiftCard() {
  const [data, setData] = useState(null);
  const fetch = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/office/opening-shift`);
      setData(r.data);
    } catch {}
  }, []);
  useEffect(() => { fetch(); const i = setInterval(fetch, 60000); return () => clearInterval(i); }, [fetch]);

  const tick = async (task_id, done) => {
    try {
      await axios.post(`${API}/office/opening-shift/tick`, { task_id, done });
      await fetch();
    } catch {}
  };

  if (!data || !data.is_target_user) return null;
  // Show if we're in the opening window OR if any tasks are still undone today (so João can finish late)
  const anyUndone = data.tasks.some(t => !t.done);
  if (!data.is_opening_window && !anyUndone) return null;

  const allDone = data.tasks.every(t => t.done);
  const borderColor = data.is_opening_window ? "#2F5D8A" : "#D1A054";
  return (
    <div className="rounded-lg p-3 border-2" style={{ borderColor, backgroundColor: "#F5F9FF" }} data-testid="opening-shift-card">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#2F5D8A] flex items-center gap-1">
          <Clock className="h-3 w-3" /> Morning Opening Routine · 8:00 – 9:00
        </p>
        <span className="text-[9px] font-bold text-gray-500">Now {data.current_boston_time} BOS</span>
      </div>
      <div className="bg-red-50 border border-red-200 rounded p-1.5 mb-2">
        <p className="text-[10px] font-bold text-red-700">⚠ No outbound calls during this block</p>
      </div>
      <div className="space-y-1">
        {data.tasks.map(t => (
          <label key={t.id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer ${t.done ? "bg-green-50" : "bg-white hover:bg-gray-50"}`} data-testid={`opening-task-${t.id}`}>
            <input type="checkbox" checked={t.done} onChange={e => tick(t.id, e.target.checked)} className="h-3.5 w-3.5 accent-[#2F5D8A]" />
            <span className={`text-xs flex-1 ${t.done ? "text-gray-400 line-through" : "text-[#1F2937] font-semibold"}`}>{t.label}</span>
            {t.done && t.completed_at && (
              <span className="text-[9px] font-mono text-green-700 bg-white/60 px-1.5 py-0.5 rounded" data-testid={`opening-task-ts-${t.id}`}>
                {new Date(t.completed_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </label>
        ))}
      </div>
      {allDone && (
        <p className="text-[10px] text-green-700 font-bold mt-2 text-center">✓ Morning routine complete — continue with normal activities</p>
      )}
    </div>
  );
}

/* ══════════════════════════════ DAILY ASSIGNMENT CARD (9:00 AM popup) ══════════════════════════════ */
function DailyAssignmentCard({ isAdmin }) {
  const [data, setData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const autoOpenedRef = useRef(false);

  const fetch = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/office/daily-assignments`);
      setData(r.data);
      setDraft(r.data.assignments || {});
    } catch {}
  }, []);
  useEffect(() => { fetch(); const i = setInterval(fetch, 60000); return () => clearInterval(i); }, [fetch]);

  // Auto-open at 9AM window if admin and no assignments set yet
  useEffect(() => {
    if (!data || autoOpenedRef.current) return;
    if (data.needs_assignment) {
      setModalOpen(true);
      autoOpenedRef.current = true;
    }
  }, [data]);

  const save = async () => {
    try {
      setSaving(true);
      await axios.post(`${API}/office/daily-assignments`, { assignments: draft });
      setModalOpen(false);
      await fetch();
    } catch {} finally { setSaving(false); }
  };

  if (!data) return null;
  const hasAssignments = Object.keys(data.assignments || {}).length > 0;

  return (
    <>
      {/* Badge card visible to everyone — shows each assignable user's task today */}
      {hasAssignments && (
        <div className="rounded-lg p-3 border border-[#A7C4A0] bg-[#F2F8F0]" data-testid="daily-assignment-card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#4A7A56] flex items-center gap-1">
              <Users className="h-3 w-3" /> Today's Assignments
            </p>
            {isAdmin && (
              <button onClick={() => setModalOpen(true)} className="text-[9px] text-[#2F5D8A] hover:underline font-bold" data-testid="daily-assignment-edit-btn">Edit</button>
            )}
          </div>
          <div className="space-y-1">
            {data.assignable_users.map(u => (
              <div key={u.email} className="flex items-center justify-between bg-white px-2 py-1 rounded">
                <span className="text-xs font-semibold text-[#1F2937]">{u.name}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#2F5D8A] text-white font-bold">
                  {data.assignments[u.email] || "—"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Admin-only banner when it's 9AM+ and nothing has been assigned yet */}
      {isAdmin && !hasAssignments && data.is_nine_am_window && (
        <button
          onClick={() => setModalOpen(true)}
          className="w-full rounded-lg p-3 border-2 border-[#2F5D8A] bg-[#2F5D8A] hover:bg-[#24496e] text-white text-left"
          data-testid="daily-assignment-open-btn"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider">⚡ Daily Assignment Pending</p>
              <p className="text-sm font-bold">Distribute João & Gabi — click to assign</p>
            </div>
            <Users className="h-5 w-5" />
          </div>
        </button>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1100] p-4" onClick={() => setModalOpen(false)} data-testid="daily-assignment-modal">
          <div className="bg-white rounded-xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <div>
              <h3 className="text-base font-bold text-[#1F2937] flex items-center gap-1.5">
                <Users className="h-4 w-4 text-[#2F5D8A]" /> Daily Assignment · {data.work_date}
              </h3>
              <p className="text-[10px] text-gray-500">Distribute the office team across today's activities.</p>
            </div>
            <div className="space-y-2">
              {data.assignable_users.map(u => (
                <div key={u.email} className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-[#1F2937] flex-1">{u.name}</span>
                  <select
                    value={draft[u.email] || ""}
                    onChange={e => setDraft({ ...draft, [u.email]: e.target.value })}
                    className="px-2 py-1.5 border border-gray-300 rounded text-xs"
                    data-testid={`daily-assignment-select-${u.email}`}
                  >
                    <option value="">— pick activity —</option>
                    {data.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setModalOpen(false)} className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-sm font-semibold rounded" data-testid="daily-assignment-cancel">Cancel</button>
              <button onClick={save} disabled={saving} className="flex-1 py-2 bg-[#2F5D8A] hover:bg-[#24496e] disabled:opacity-50 text-white text-sm font-bold rounded" data-testid="daily-assignment-save">
                {saving ? "Saving..." : "Save assignments"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

